import { z } from 'zod'
import { addDays, addMonths, endOfMonth, isIsoDate, parseIsoDate } from '../../lib/dates'
import type { DateRange } from '../../ui/date-range'

/**
 * Every report keeps its period (and the ledger its account, party and page) in the URL: shareable,
 * back-button safe. Invalid values fall back to the default instead of breaking the page; defaults
 * are applied by the pages so they never clutter the URL (as the trial balance does).
 */
const isoDate = z
  .string()
  .refine((v) => isIsoDate(v))
  .optional()
  .catch(undefined)

/** Profit and loss: the period (default this month) and an optional comparison period. */
export const profitAndLossSearchSchema = z.object({
  from: isoDate,
  to: isoDate,
  compareFrom: isoDate,
  compareTo: isoDate,
})
export type ProfitAndLossSearch = z.infer<typeof profitAndLossSearchSchema>

/** Balance sheet: the as-of date (default today). */
export const balanceSheetSearchSchema = z.object({ asOf: isoDate })
export type BalanceSheetSearch = z.infer<typeof balanceSheetSearchSchema>

export const LEDGER_PAGE_SIZES = [50, 100, 200] as const
/** The API's default page size. */
export const LEDGER_DEFAULT_PAGE_SIZE = 100

export const PARTY_TYPES = ['CUSTOMER', 'SUPPLIER'] as const
export type PartyType = (typeof PARTY_TYPES)[number]

/** General ledger (account statement): account, period (default this month), party, page. */
export const generalLedgerSearchSchema = z.object({
  accountId: z.uuid().optional().catch(undefined),
  from: isoDate,
  to: isoDate,
  partyType: z.enum(PARTY_TYPES).optional().catch(undefined),
  partyId: z.uuid().optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize: z
    .number()
    .refine((n): n is (typeof LEDGER_PAGE_SIZES)[number] =>
      (LEDGER_PAGE_SIZES as readonly number[]).includes(n),
    )
    .optional()
    .catch(undefined),
})
export type GeneralLedgerSearch = z.infer<typeof generalLedgerSearchSchema>

/** VAT return: the period (default last month). */
export const vatReturnSearchSchema = z.object({ from: isoDate, to: isoDate })
export type VatReturnSearch = z.infer<typeof vatReturnSearchSchema>

/** Cash flow statement: the period (default this month). */
export const cashFlowSearchSchema = z.object({ from: isoDate, to: isoDate })
export type CashFlowSearch = z.infer<typeof cashFlowSearchSchema>

// ---------------------------------------------------------------------------------------------
// Comparison periods (profit and loss)

export const COMPARE_MODES = ['off', 'previous', 'lastYear', 'custom'] as const
export type CompareMode = (typeof COMPARE_MODES)[number]

function dayNumber(iso: string): number {
  const parts = parseIsoDate(iso)
  if (!parts) throw new RangeError(`Not an ISO date: ${iso}`)
  return Date.UTC(parts.year, parts.month - 1, parts.day) / 86_400_000
}

/** Whole calendar months (from the 1st to a month end): the number of months, else null. */
function wholeMonths({ from, to }: DateRange): number | null {
  const start = parseIsoDate(from)
  const end = parseIsoDate(to)
  if (!start || !end || start.day !== 1 || endOfMonth(to) !== to) return null
  return end.year * 12 + end.month - (start.year * 12 + start.month) + 1
}

/**
 * The period of equal length just before this one. Whole months compare with the same number of
 * whole months (September → August, a year → the year before); any other range with the same
 * number of days ending the day before it starts.
 */
export function previousPeriod(range: DateRange): DateRange {
  const months = wholeMonths(range)
  if (months !== null) {
    return { from: addMonths(range.from, -months), to: endOfMonth(addMonths(range.from, -1)) }
  }
  const days = dayNumber(range.to) - dayNumber(range.from) + 1
  return { from: addDays(range.from, -days), to: addDays(range.from, -1) }
}

/** The same dates a year earlier; a period ending on a month end ends on that month's end. */
export function samePeriodLastYear(range: DateRange): DateRange {
  const to = addMonths(range.to, -12)
  return {
    from: addMonths(range.from, -12),
    to: endOfMonth(range.to) === range.to ? endOfMonth(to) : to,
  }
}

/**
 * Which comparison the URL describes: none, one of the two derived periods, or custom dates. For a
 * twelve-month period both derived periods are the same dates; that reads as "same period last year".
 */
export function compareModeOf(range: DateRange, compare: DateRange | null): CompareMode {
  if (!compare) return 'off'
  const same = (other: DateRange) => other.from === compare.from && other.to === compare.to
  if (same(samePeriodLastYear(range))) return 'lastYear'
  if (same(previousPeriod(range))) return 'previous'
  return 'custom'
}

/**
 * The comparison period for a mode. A derived mode follows the period; custom keeps the dates it
 * has (or starts from the previous period).
 */
export function comparisonFor(
  mode: CompareMode,
  range: DateRange,
  current: DateRange | null,
): DateRange | null {
  switch (mode) {
    case 'off':
      return null
    case 'previous':
      return previousPeriod(range)
    case 'lastYear':
      return samePeriodLastYear(range)
    case 'custom':
      return current ?? previousPeriod(range)
  }
}
